import { useEffect, useState, ForwardedRef, forwardRef } from 'react'
import { Input } from 'barte-design-system'

import type { InputNumberProps } from './input-number.type'

export const InputNumber = forwardRef(({
    className,
    disabled,
    onChange,
    value,
    placeholder,
    onBlur,
    prefix = '',
    suffix = '',
    locale = 'pt-BR',
    decimalPrecision = 2,
    name,
    min,
    max,
    onKeyDown,
    label,
    error,
    helperText,
}: InputNumberProps, ref: ForwardedRef<HTMLInputElement>) => {
    const [localValue, setLocalValue] = useState('')

    const divisor = Math.pow(10, decimalPrecision)

    const cleanInput = (input: string) => {
        let cleaned = input
        if (prefix) cleaned = cleaned.replace(prefix, '')
        if (suffix) cleaned = cleaned.replace(suffix, '')
        const isNegative = cleaned.includes('-')
        cleaned = cleaned.replace(/\D/g, '')
        if (isNegative) {
            cleaned = '-' + cleaned
        }
        return cleaned
    }

    const formatNumber = (num: string) => {
        if (num === '' || num === '-') return num

        let cleaned = cleanInput(num)
        if (cleaned === '' || cleaned === '-') return cleaned

        const isNegative = cleaned.startsWith('-')

        let numValue = parseInt(cleaned, 10) / divisor
        if (Number.isNaN(numValue)) numValue = 0

        numValue = parseFloat(numValue.toFixed(decimalPrecision))

        const absoluteValue = Math.abs(numValue)

        return (
            prefix +
            (isNegative ? '-' : '') +
            absoluteValue.toLocaleString(locale, {
                minimumFractionDigits: decimalPrecision,
                maximumFractionDigits: decimalPrecision,
            }) +
            suffix
        )
    }

    useEffect(() => {
        if (value !== undefined && value !== null) {
            const expectedStr = formatNumber(`${Math.round((value as number) * divisor)}`);
            
            if (localValue !== expectedStr) {
                setLocalValue(expectedStr);
            }
        } else {
            if (localValue !== '-' && localValue !== '') {
                setLocalValue('')
            }
        }
    }, [value, prefix, suffix, divisor, decimalPrecision, locale])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (disabled) return
        const cleaned = cleanInput(e.target.value)
        const numValue = cleaned === '' || cleaned === '-' ? 0 : parseInt(cleaned, 10)

        if (min !== undefined && numValue < min) {
            return
        }

        if (max !== undefined && (numValue / divisor) > max) {
            return
        }

        setLocalValue(formatNumber(cleaned))

        if (onChange) {
            onChange(cleaned === '' || cleaned === '-' ? undefined : numValue / divisor)
        }

        setTimeout(() => {
            if (e?.target && e?.target?.setSelectionRange) {
                const newPosition = e.target.value.length - suffix.length
                if (newPosition >= 0) {
                    e.target.setSelectionRange(newPosition, newPosition)
                }
            }
        }, 0)
    }

    return (
        <Input
            disabled={disabled}
            onChange={handleChange}
            value={localValue}
            placeholder={placeholder}
            onBlur={onBlur}
            name={name}
            onKeyDown={onKeyDown}
            label={label}
            className={className}
            error={error}
            helpText={helperText}
            ref={ref}
        />
    )
})

InputNumber.displayName = 'InputNumber'